/**
 * Application root: providers, routing and the top-level gates.
 *
 * Two gates sit in front of everything. The setup gate routes to the wizard
 * until an owner exists, so a fresh install cannot land on an empty dashboard
 * and leave the operator guessing. The auth gate routes to sign-in when the
 * session is gone. Both are resolved before any route renders, which is what
 * stops a half-configured console flashing real screens with no data in them.
 */

import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { I18nProvider, useT } from '@/i18n/context'
import { Shell } from './Shell'
import { api, ApiError, setUnauthorizedHandler } from '@/api/client'
import { useEventStreamConnection } from '@/hooks/useSystemStatus'
import { Spinner } from '@/components/primitives'
import { ConfirmProvider } from '@/components/confirm'

import { AccountsPage } from '@/routes/AccountsPage'
import { ModelsPage } from '@/routes/ModelsPage'
import { FilesPage } from '@/routes/hostfiles/FilesPage'
import { TerminalsPage } from '@/routes/TerminalsPage'
import { HostsPage } from '@/routes/HostsPage'
import { ProjectsPage } from '@/routes/ProjectsPage'
import { LiveOpsPage } from '@/routes/LiveOpsPage'
import { DashboardPage } from '@/routes/DashboardPage'
import { TasksPage } from '@/routes/TasksPage'
import { AuditPage } from '@/routes/AuditPage'
import { ProfilePage } from '@/routes/ProfilePage'
import { SettingsPage } from '@/routes/SettingsPage'
import { LoginPage } from '@/routes/LoginPage'
import { SetupWizard } from '@/routes/SetupWizard'
import { NotFoundPage } from '@/routes/NotFoundPage'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Live data arrives over the event stream, so aggressive refetching would
      // duplicate work the socket already does. Windows focus refetch stays on
      // for the case where the socket was down while the tab was hidden.
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && !error.isRetryable) return false
        return failureCount < 2
      },
    },
  },
})

type Gate = 'checking' | 'setup-required' | 'unauthenticated' | 'ready'

/**
 * Mirrors api.SetupStatusResponse. Field names are the server's.
 *
 * An earlier version of this type declared `setupComplete` and `authenticated`,
 * neither of which any handler sends — `setupCompleted` differs by one letter
 * and the session state lives on a different endpoint entirely. Both read as
 * undefined, so the gate concluded "setup required" on every load and would
 * have sent the operator back to the wizard forever once they finished it.
 */
interface SetupStatusResponse {
  available: boolean
  bootstrapCompleted: boolean
  tokenOutstanding: boolean
  tokenExpiresAt?: string
  setupCompleted: boolean
  csrfToken?: string
}

/** Mirrors api.SessionResponseBody. Anonymous callers get 200 with
 *  authenticated:false — being nobody is an answer, not a failure. */
interface SessionResponse {
  authenticated: boolean
  actor?: { username: string; role: string }
  csrfToken?: string
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <ConfirmProvider>
          <BrowserRouter>
            {/* One skip link for the whole document, rendered above every gate.
              It used to live inside the shell, which meant it did not exist
              during boot, on sign-in or in the wizard — and worse, that the
              boot-to-shell handover destroyed the element a keyboard user had
              just focused, dropping them back to the top of the page. Every
              screen's content region answers to #main, so one link serves all
              of them and survives the transitions between them. */}
            <SkipLink />
            <Gatekeeper />
          </BrowserRouter>
        </ConfirmProvider>
      </I18nProvider>
    </QueryClientProvider>
  )
}

function SkipLink() {
  const t = useT()
  return (
    <a className="k-skip-link" href="#main">
      {t('nav.skipToContent')}
    </a>
  )
}

function Gatekeeper() {
  const [gate, setGate] = useState<Gate>('checking')
  const navigate = useNavigate()

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setGate('unauthenticated')
      navigate('/login', { replace: true })
    })
  }, [navigate])

  useEffect(() => {
    let cancelled = false
    // Two questions, two endpoints: /setup/status knows whether the install is
    // finished, /auth/session knows who is asking. Neither answers the other,
    // and an earlier version tried to read both from the first one.
    Promise.all([
      api.get<SetupStatusResponse>('/setup/status'),
      api.get<SessionResponse>('/auth/session').catch(() => ({ authenticated: false })),
    ])
      .then(([setup, session]) => {
        if (cancelled) return
        if (!setup.setupCompleted) setGate('setup-required')
        else if (!session.authenticated) setGate('unauthenticated')
        else setGate('ready')
      })
      .catch(() => {
        // If the status endpoint itself is unreachable the safest assumption is
        // "not signed in": showing the console with no data would be worse than
        // showing a sign-in form that fails honestly.
        if (!cancelled) setGate('unauthenticated')
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (gate === 'checking') return <BootGate />

  if (gate === 'setup-required') {
    return (
      <Routes>
        <Route path="/setup/*" element={<SetupWizard onComplete={() => setGate('unauthenticated')} />} />
        <Route path="*" element={<Navigate to="/setup" replace />} />
      </Routes>
    )
  }

  if (gate === 'unauthenticated') {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage onAuthenticated={() => setGate('ready')} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  return <AuthenticatedApp />
}

/**
 * The screen the console shows while it works out where it stands.
 *
 * It is a full screen, not a placeholder, and it is built like one: a `main`
 * landmark for the document-level skip link to answer to, and a translated
 * status message that the spinner is only the picture of. Before this it was a
 * bare div holding a spinner labelled with the untranslated string "loading" —
 * a document with no landmark at all, which is nothing to orient in even for
 * the second it is on screen.
 */
function BootGate() {
  const t = useT()
  return (
    <div className="k-boot">
      <main id="main" className="k-boot__main" tabIndex={-1}>
        <Spinner size={22} label={t('state.loading')} />
      </main>
    </div>
  )
}

function AuthenticatedApp() {
  useEventStreamConnection()

  return (
    <Routes>
      <Route element={<Shell />}>
        {/* The dashboard is the landing page: a day starts with "what happened,
            what is waiting, what should I do next", and that is what it answers
            — with the cockpit one click away from every row. The cockpit was the
            landing page while there was nothing above it to land on. */}
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="tasks/:taskId" element={<TasksPage />} />
        <Route path="live" element={<LiveOpsPage />} />
        <Route path="live/:sessionId" element={<LiveOpsPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/:projectId" element={<ProjectsPage />} />
        <Route path="projects/:projectId/:tab" element={<ProjectsPage />} />
        <Route path="accounts" element={<AccountsPage />} />
        <Route path="models" element={<ModelsPage />} />
        <Route path="terminals" element={<TerminalsPage />} />
        {/* Its own destination rather than a panel inside /live: that screen is
            the densest in the console, and moving a four-gigabyte file is not a
            thing to do in a strip under a wall of terminals. */}
        <Route path="files" element={<FilesPage />} />
        <Route path="hosts" element={<HostsPage />} />
        <Route path="hosts/:hostId" element={<HostsPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/:section" element={<SettingsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      {/* A signed-in operator hitting /login or /setup belongs on the console. */}
      <Route path="/login" element={<Navigate to="/dashboard" replace />} />
      <Route path="/setup/*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}
